import { BillStatus } from "@redotech/cockroachdb-codegen/tables/billing/bill-status";
import { BillType } from "@redotech/cockroachdb-codegen/tables/billing/bill-type";
import { BillingChargeSubtype } from "@redotech/cockroachdb-codegen/tables/billing/billing-charge-subtype";
import { isEnumValue } from "@redotech/util/enum";
import { pascalToSnakeCase } from "@redotech/util/string";
import { assertNever } from "@redotech/util/type";
import { SubscriptionProduct } from "./unified-billing/enums";

export enum BillingChargeType {
  Coverage = "Coverage",
  Return = "Return",
  ShippingLabel = "ShippingLabel",
  Subscription = "Subscription",
  Campaign = "Campaign",
  OrderTracking = "OrderTracking",
  CheckoutOptimization = "CheckoutOptimization",
  Recover = "Recover",
  Credit = "Credit",
  Usage = "Usage",
  Supplies = "Supplies",
}

export const chargeTypeLabels: Record<BillingChargeType, string> = {
  [BillingChargeType.Coverage]: "Coverage",
  [BillingChargeType.Return]: "Returns",
  [BillingChargeType.ShippingLabel]: "Shipping Labels",
  [BillingChargeType.Subscription]: "Subscription",
  [BillingChargeType.Campaign]: "Campaign",
  [BillingChargeType.OrderTracking]: "Order Tracking",
  [BillingChargeType.CheckoutOptimization]: "Checkout Optimization",
  [BillingChargeType.Recover]: "Recover",
  [BillingChargeType.Credit]: "Credit",
  [BillingChargeType.Usage]: "Usage",
  [BillingChargeType.Supplies]: "Supplies",
};

export function getChargeTypeLabel(
  chargeType: BillingChargeType | string,
): string {
  if (isEnumValue(chargeType, BillingChargeType)) {
    return chargeTypeLabels[chargeType as BillingChargeType];
  }
  return chargeType;
}
/**
 * Display-only subtypes that predate `BillingChargeSubtype` and still appear on
 * historical charges. Kept so old invoices do not regress to raw enum text.
 */
type LegacyChargeSubtype =
  | "merchant_covered_label_damaged"
  | "merchant_covered_label_wrong_product";

/**
 * Every subtype a merchant can see, in the merchant's language. Typed against
 * the DB enum so a new subtype fails the build until someone names it.
 */
export const chargeSubtypeLabels: Record<
  BillingChargeSubtype | LegacyChargeSubtype,
  string
> = {
  [BillingChargeSubtype.CanceledPickupRefund]: "Pickup Refund",
  [BillingChargeSubtype.CanceledReturnLabelRefund]: "Return Label Refund",
  [BillingChargeSubtype.CancelledExtendedWarrantyRefund]:
    "Extended Warranty Refund",
  [BillingChargeSubtype.CancelledFinalSaleReturnCoverageRefund]:
    "Final Sale Coverage Refund",
  [BillingChargeSubtype.CancelledFinalSaleRevShareCoverageRefund]:
    "Final Sale Coverage Revenue Share Refund",
  [BillingChargeSubtype.CancelledInstantExchangeRecovery]:
    "Instant Exchange Recovery Reversal",
  [BillingChargeSubtype.CancelledOrderExtendedWarrantyRefund]:
    "Extended Warranty Refund (Cancelled Order)",
  [BillingChargeSubtype.CancelledOrderFinalSaleReturnCoverageRefund]:
    "Final Sale Coverage Refund (Cancelled Order)",
  [BillingChargeSubtype.CancelledOrderInstantExchangeRecovery]:
    "Instant Exchange Recovery (Cancelled Order)",
  [BillingChargeSubtype.CancelledOrderPackageProtectionPlusRefund]:
    "Package Protection Plus Refund (Cancelled Order)",
  [BillingChargeSubtype.CancelledOrderRmrConsolidationCoverageShareRefund]:
    "Consolidation Coverage Share Refund (Cancelled Order)",
  [BillingChargeSubtype.CancelledOrderRmrProcessingCoverageShareRefund]:
    "Processing Coverage Share Refund (Cancelled Order)",
  [BillingChargeSubtype.CancelledPackageProtectionPlusRefund]:
    "Package Protection Plus Refund",
  [BillingChargeSubtype.CancelledPackageProtectionRefund]:
    "Package Protection Refund",
  [BillingChargeSubtype.CancelledPackageProtectionRevShareCoverageRefund]:
    "Package Protection Revenue Share Refund",
  [BillingChargeSubtype.CancelledPpPlusRevShareCoverageRefund]:
    "Package Protection Plus Revenue Share Refund",
  [BillingChargeSubtype.CancelledRevShareCoverageRefund]:
    "Coverage Revenue Share Refund",
  [BillingChargeSubtype.CardProcessingFee]: "Card Processing Fee",
  [BillingChargeSubtype.CarrierClaimRefund]: "Carrier Claim Refund",
  [BillingChargeSubtype.CarrierPickup]: "Carrier Pickup",
  [BillingChargeSubtype.ChargeLabel]: "Shipping Label",
  [BillingChargeSubtype.ChopAdjustment]: "Shipping Adjustment",
  [BillingChargeSubtype.ClaimCommission]: "Claim Commission",
  [BillingChargeSubtype.ClaimPayout]: "Claim Payout",
  [BillingChargeSubtype.ClaimsAdjustment]: "Claims Adjustment",
  [BillingChargeSubtype.ConsolidationShipping]: "Consolidation Shipping",
  [BillingChargeSubtype.CoverageAdjustment]: "Coverage Adjustment",
  [BillingChargeSubtype.CoverageRefundForCancelledOrder]:
    "Coverage Refund (Cancelled Order)",
  [BillingChargeSubtype.CustomerPaidLabel]: "Customer Paid Label",
  [BillingChargeSubtype.Damaged]: "Damaged Return",
  [BillingChargeSubtype.DeniedClaimPackageProtectionPlusRefund]:
    "Package Protection Plus Refund (Denied Claim)",
  [BillingChargeSubtype.DutyDrawbackCredit]: "Duty Drawback Credit",
  [BillingChargeSubtype.ExchangeShippingCredit]: "Exchange Shipping Credit",
  [BillingChargeSubtype.ExtendedWarranty]: "Extended Warranty",
  [BillingChargeSubtype.FinalSaleCredit]: "Final Sale Credit",
  [BillingChargeSubtype.FinalSaleCreditAdjustment]: "Dynamic FSR Adjustment",
  [BillingChargeSubtype.FinalSaleReturnCoverage]: "Final Sale",
  [BillingChargeSubtype.FinalSaleRevShareCoverage]:
    "Final Sale Coverage Revenue Share",
  [BillingChargeSubtype.FreeTrialCredit]: "Free Trial Credit",
  [BillingChargeSubtype.FreightPurchase]: "Freight",
  [BillingChargeSubtype.FsrAdjustment]: "Final Sale Returns Adjustment",
  [BillingChargeSubtype.ImplementationFee]: "Implementation Fee",
  [BillingChargeSubtype.InstantExchangeRecovery]: "Instant Exchange Recovery",
  [BillingChargeSubtype.InstantRefund]: "Instant Refund",
  [BillingChargeSubtype.Insurance]: "Shipping Insurance",
  [BillingChargeSubtype.LandedCostDuties]: "Duties",
  [BillingChargeSubtype.LandedCostFees]: "Landed Cost Fees",
  [BillingChargeSubtype.LandedCostTaxes]: "Import Taxes",
  [BillingChargeSubtype.ManagedClaimCredit]: "Managed Claim Credit",
  [BillingChargeSubtype.ManualAdjustment]: "Manual Adjustment",
  [BillingChargeSubtype.ManualRefund]: "Manual Refund",
  [BillingChargeSubtype.MarketingAdjustment]: "Marketing Adjustment",
  [BillingChargeSubtype.MerchantCarrierFee]: "Carrier Fee",
  [BillingChargeSubtype.MerchantCarrierLabelCredit]: "Carrier Label Credit",
  [BillingChargeSubtype.MerchantCoveredLabel]: "Merchant Covered Label",
  [BillingChargeSubtype.MerchantCreatedReturnLabel]:
    "Merchant Created Return Label",
  [BillingChargeSubtype.MerchantIncentive]: "Merchant Incentive",
  [BillingChargeSubtype.MerchantPaidPpPlusPerOrder]:
    "Package Protection Plus (Merchant Paid)",
  [BillingChargeSubtype.Miscellaneous]: "Miscellaneous",
  [BillingChargeSubtype.NonCoverageAdjustment]: "Non-Coverage Adjustment",
  [BillingChargeSubtype.OmsAdjustment]: "Shipping Adjustment",
  [BillingChargeSubtype.OrderTracking]: "Order Tracking",
  [BillingChargeSubtype.OrderTrackingAdjustment]: "Order Tracking Adjustment",
  [BillingChargeSubtype.OtherAdjustment]: "Other Adjustment",
  [BillingChargeSubtype.OutboundLabel]: "Outbound Label",
  [BillingChargeSubtype.PackagePickup]: "Package Pickup",
  [BillingChargeSubtype.PackageProtection]: "Package Protection",
  [BillingChargeSubtype.PackageProtectionPlus]: "Package Protection Plus",
  [BillingChargeSubtype.PackageProtectionRevShareCoverage]:
    "Package Protection Revenue Share",
  [BillingChargeSubtype.PerEmail]: "Per Email",
  [BillingChargeSubtype.PerExchangePayoutFee]: "Exchange Payout Fee",
  [BillingChargeSubtype.PerOrder]: "Per Order",
  [BillingChargeSubtype.PerRecycleRequestFee]: "Recycle Request Fee",
  [BillingChargeSubtype.PerReturnFee]: "Per Return Fee",
  [BillingChargeSubtype.PerSms]: "Per SMS",
  [BillingChargeSubtype.PersonalizedInsertFee]: "Personalized Insert Fee",
  [BillingChargeSubtype.PersonalizedInsertRevenueShare]:
    "Personalized Insert Revenue Share",
  [BillingChargeSubtype.PointOfSale]: "Point of Sale",
  [BillingChargeSubtype.PpPlusAdjustment]: "Package Protection Plus Adjustment",
  [BillingChargeSubtype.PpPlusRevShareCoverage]:
    "Package Protection Plus Revenue Share",
  [BillingChargeSubtype.PromotionalCredit]: "Promotional Credit",
  [BillingChargeSubtype.PurchaseOrderItem]: "Item",
  [BillingChargeSubtype.PurchaseOrderShipping]: "Shipping",
  [BillingChargeSubtype.ReFulfillmentFixedFee]: "Refulfillment Fee",
  [BillingChargeSubtype.ReFulfillmentRevenueShare]:
    "Refulfillment Revenue Share",
  [BillingChargeSubtype.ReFulfillmentShippingAdjustment]:
    "Refulfillment Shipping Adjustment",
  [BillingChargeSubtype.RedeemPoints]: "Points Redeemed",
  [BillingChargeSubtype.RedoManagedReturnsCoverage]: "Managed Returns Coverage",
  [BillingChargeSubtype.RedoSupply]: "Redo Supplies",
  [BillingChargeSubtype.ReduceBalance]: "Balance Reduction",
  [BillingChargeSubtype.Refund]: "Refund",
  [BillingChargeSubtype.RepairFeeCredit]: "Repair Fee Credit",
  [BillingChargeSubtype.ReturnCoverage]: "Return Coverage",
  [BillingChargeSubtype.ReturnFraudPrevention]: "Return Fraud Prevention",
  [BillingChargeSubtype.ReturnLabel]: "Return Label",
  [BillingChargeSubtype.ReturnLabelAdjustment]: "Return Label Adjustment",
  [BillingChargeSubtype.ReturnLabelMarkupRevenue]:
    "Return Label Markup Revenue",
  [BillingChargeSubtype.ReturnsProcessingFee]: "Processing Fee",
  [BillingChargeSubtype.RevShareCoverage]: "Coverage Revenue Share",
  [BillingChargeSubtype.RmrAdjustment]: "Managed Returns Adjustment",
  [BillingChargeSubtype.RmrConsolidationCoverageShare]:
    "Consolidation Coverage Share",
  [BillingChargeSubtype.RmrProcessingCoverageShare]:
    "Processing Coverage Share",
  [BillingChargeSubtype.SalesTax]: "Sales Tax",
  [BillingChargeSubtype.ShippingCostShare]: "Shipping Cost Share",
  [BillingChargeSubtype.ShopperAiAdjustment]: "Shopper AI Adjustment",
  [BillingChargeSubtype.SubscriptionAdjustment]: "Subscription Adjustment",
  [BillingChargeSubtype.SubscriptionFee]: "Subscription Fee",
  [BillingChargeSubtype.Surcharge]: "Surcharge",
  [BillingChargeSubtype.TransactionFailureClawback]: "Failed Payment Reversal",
  [BillingChargeSubtype.UpsellRevenue]: "Upsell Revenue",
  [BillingChargeSubtype.UpsellRevenueRefunded]: "Upsell Revenue Refunded",
  [BillingChargeSubtype.UspsPcPostageFee]: "USPS Postage Fee",
  [BillingChargeSubtype.VoidFreightPurchase]: "Freight Void",
  [BillingChargeSubtype.VoidInsurance]: "Shipping Insurance Void",
  [BillingChargeSubtype.VoidLabel]: "Label Void",
  [BillingChargeSubtype.VoidLandedCostDuties]: "Duties Refund",
  [BillingChargeSubtype.VoidLandedCostFees]: "Landed Cost Fees Refund",
  [BillingChargeSubtype.VoidLandedCostTaxes]: "Import Taxes Refund",
  [BillingChargeSubtype.VoidUsedLabel]: "Used Label Void",
  [BillingChargeSubtype.WarrantiesAdjustment]: "Warranties Adjustment",
  [BillingChargeSubtype.WmsFee]: "Warehouse Fee",
  [BillingChargeSubtype.Writeoff]: "Write-Off",
  [BillingChargeSubtype.WrongProduct]: "Wrong Product Return",
  [BillingChargeSubtype.ZeroOutAccountBalance]: "Balance Zeroed Out",
  merchant_covered_label_damaged: "Damaged Label",
  merchant_covered_label_wrong_product: "Wrong Product Label",
};

/**
 * Charges reach the UI in two dialects: snake_case from raw SQL, PascalCase
 * from the Prisma ORM. Normalize before lookup so both find the same label.
 */
export function getChargeSubtypeLabel(subtype: string): string {
  return (
    chargeSubtypeLabels[
      pascalToSnakeCase(subtype) as BillingChargeSubtype | LegacyChargeSubtype
    ] ?? subtype
  );
}

export const billTypeLabels: Record<BillType, string> = {
  [BillType.Shipping]: "Shipping",
  [BillType.ReturnShipping]: "Returns",
  [BillType.Software]: "Software",
  [BillType.PurchaseOrder]: "Purchase Order",
};

export function getBillTypeLabel(billType: BillType | string): string {
  if (isEnumValue(billType, BillType)) {
    return billTypeLabels[billType as BillType];
  }
  return billType;
}

export const billStatusLabels: Record<BillStatus, string> = {
  [BillStatus.Open]: "Upcoming",
  [BillStatus.Closed]: "Pending",
  [BillStatus.Paid]: "Paid",
  [BillStatus.Void]: "Void",
  [BillStatus.Failed]: "Failed",
  [BillStatus.Refunded]: "Refunded",
  [BillStatus.PartiallyRefunded]: "Partially Refunded",
};

export type ChargeTypeBadgeColor =
  | "blueLight"
  | "blue"
  | "indigo"
  | "purple"
  | "pink"
  | "orange"
  | "brand"
  | "grayBlue"
  | "success"
  | "gray";

export const chargeTypeColors: Record<BillingChargeType, ChargeTypeBadgeColor> =
  {
    [BillingChargeType.Coverage]: "blueLight",
    [BillingChargeType.Return]: "blue",
    [BillingChargeType.ShippingLabel]: "indigo",
    [BillingChargeType.Subscription]: "purple",
    [BillingChargeType.Campaign]: "pink",
    [BillingChargeType.OrderTracking]: "orange",
    [BillingChargeType.CheckoutOptimization]: "brand",
    [BillingChargeType.Recover]: "grayBlue",
    [BillingChargeType.Credit]: "success",
    [BillingChargeType.Usage]: "orange",
    [BillingChargeType.Supplies]: "gray",
  };

export function getChargeTypeColor(
  chargeType: BillingChargeType | string,
): ChargeTypeBadgeColor {
  if (isEnumValue(chargeType, BillingChargeType)) {
    return chargeTypeColors[chargeType as BillingChargeType];
  }
  return "gray";
}

// Subtypes are grouped by their parent charge type for consistent color coding.
const subtypeToChargeType: Partial<
  Record<BillingChargeSubtype | LegacyChargeSubtype, BillingChargeType>
> = {
  [BillingChargeSubtype.ReturnCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PackageProtectionPlus]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PackageProtection]: BillingChargeType.Coverage,
  [BillingChargeSubtype.FinalSaleReturnCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.ExtendedWarranty]: BillingChargeType.Coverage,
  [BillingChargeSubtype.UpsellRevenue]: BillingChargeType.Coverage,
  [BillingChargeSubtype.FinalSaleCreditAdjustment]: BillingChargeType.Coverage,
  [BillingChargeSubtype.RevShareCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.FinalSaleRevShareCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PpPlusRevShareCoverage]: BillingChargeType.Coverage,
  [BillingChargeSubtype.PackageProtectionRevShareCoverage]:
    BillingChargeType.Coverage,
  [BillingChargeSubtype.ManagedClaimCredit]: BillingChargeType.Return,
  [BillingChargeSubtype.PerOrder]: BillingChargeType.Return,
  [BillingChargeSubtype.PerReturnFee]: BillingChargeType.Return,
  [BillingChargeSubtype.ReturnsProcessingFee]: BillingChargeType.Return,
  [BillingChargeSubtype.DutyDrawbackCredit]: BillingChargeType.Return,
  [BillingChargeSubtype.MerchantCoveredLabel]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.MerchantCarrierLabelCredit]:
    BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.ShippingCostShare]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.ReturnLabelMarkupRevenue]:
    BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.CustomerPaidLabel]: BillingChargeType.ShippingLabel,
  [BillingChargeSubtype.PerSms]: BillingChargeType.Campaign,
  [BillingChargeSubtype.PerEmail]: BillingChargeType.Campaign,
  [BillingChargeSubtype.PurchaseOrderItem]: BillingChargeType.Supplies,
  [BillingChargeSubtype.PurchaseOrderShipping]: BillingChargeType.Supplies,
  [BillingChargeSubtype.SalesTax]: BillingChargeType.Supplies,
  merchant_covered_label_damaged: BillingChargeType.ShippingLabel,
  merchant_covered_label_wrong_product: BillingChargeType.ShippingLabel,
};

export function getChargeSubtypeColor(subtype: string): ChargeTypeBadgeColor {
  const parentType =
    subtypeToChargeType[
      pascalToSnakeCase(subtype) as BillingChargeSubtype | LegacyChargeSubtype
    ];
  return parentType ? chargeTypeColors[parentType] : "gray";
}

export function getBillStatusLabel(status: BillStatus | string): string {
  if (isEnumValue(status, BillStatus)) {
    return billStatusLabels[status as BillStatus];
  }
  return status;
}

export type BillStatusTheme = "success" | "warning" | "error" | "default";

export const billStatusThemes: Record<BillStatus, BillStatusTheme> = {
  [BillStatus.Open]: "warning",
  [BillStatus.Closed]: "warning",
  [BillStatus.Paid]: "success",
  [BillStatus.Void]: "error",
  [BillStatus.Failed]: "error",
  [BillStatus.Refunded]: "success",
  [BillStatus.PartiallyRefunded]: "success",
};

export function getBillStatusTheme(
  status: BillStatus | string,
): BillStatusTheme {
  if (isEnumValue(status, BillStatus)) {
    return billStatusThemes[status as BillStatus];
  }
  return "default";
}

/**
 * The status a merchant actually sees, derived from the persisted `BillStatus`
 * plus whether the bill has any billing transaction. The persisted status
 * cannot distinguish a closed bill that is awaiting its first (manual)
 * collection from one whose charge is in flight — both sit at `Closed` — so the
 * display layer splits them: a closed bill with no transaction is `New`
 * (awaiting payment, payable now), one with a transaction is `Pending`
 * (in-flight, e.g. an ACH charge settling). This is the single source of truth
 * for both the unified invoices UI and the legacy payouts status
 * (`payoutStatusFromBill`).
 */
export enum BillDisplayStatus {
  New = "New",
  Open = "Open",
  Pending = "Pending",
  Paid = "Paid",
  Void = "Void",
  Failed = "Failed",
  Refunded = "Refunded",
  PartiallyRefunded = "PartiallyRefunded",
  // A closed period that netted a credit (merchant owed): nothing was charged
  // and the negative balance carries to the next period. Distinct from `New`
  // (which is a closed-but-unpaid amount OWED, awaiting payment).
  Credit = "Credit",
  // Closed on cadence, charge intentionally held (new/absent card); the sweep
  // collects it automatically once the card is chargeable.
  Deferred = "Deferred",
}

export function deriveBillDisplayStatus(
  status: BillStatus,
  hasTransactions: boolean,
  // The bill's net balance. A closed bill with no real transaction and a
  // negative balance is a CREDIT (carried forward), not an amount-owed `New`.
  balanceEnd?: number,
  // Merchant-display only: a deferred closed bill reads `Deferred`, not `New`.
  // Omitted by `payoutStatusFromBill` so the sweep's `new` guard still fires.
  deferred?: boolean,
): BillDisplayStatus {
  switch (status) {
    case BillStatus.Open:
      return BillDisplayStatus.Open;
    case BillStatus.Closed:
      if (hasTransactions) {
        return BillDisplayStatus.Pending;
      }
      if (balanceEnd !== undefined && balanceEnd < 0) {
        return BillDisplayStatus.Credit;
      }
      // Closed at zero owes nothing and never will, so `New` would invite a $0
      // payment. Deferring a charge that does not exist is meaningless too.
      if (balanceEnd === 0) {
        return BillDisplayStatus.Paid;
      }
      return deferred ? BillDisplayStatus.Deferred : BillDisplayStatus.New;
    case BillStatus.Paid:
      return BillDisplayStatus.Paid;
    case BillStatus.Void:
      return BillDisplayStatus.Void;
    case BillStatus.Failed:
      return BillDisplayStatus.Failed;
    case BillStatus.Refunded:
      return BillDisplayStatus.Refunded;
    case BillStatus.PartiallyRefunded:
      return BillDisplayStatus.PartiallyRefunded;
    default:
      return assertNever(status);
  }
}

export const billDisplayStatusLabels: Record<BillDisplayStatus, string> = {
  [BillDisplayStatus.New]: "New",
  // Still accruing — merchant-facing wording for a period that hasn't closed.
  [BillDisplayStatus.Open]: "Upcoming",
  [BillDisplayStatus.Pending]: "Pending",
  [BillDisplayStatus.Paid]: "Paid",
  [BillDisplayStatus.Void]: "Void",
  [BillDisplayStatus.Failed]: "Failed",
  [BillDisplayStatus.Refunded]: "Refunded",
  [BillDisplayStatus.PartiallyRefunded]: "Partially Refunded",
  [BillDisplayStatus.Credit]: "Credit",
  [BillDisplayStatus.Deferred]: "Scheduled",
};

export const billDisplayStatusThemes: Record<
  BillDisplayStatus,
  BillStatusTheme
> = {
  [BillDisplayStatus.New]: "warning",
  [BillDisplayStatus.Open]: "warning",
  [BillDisplayStatus.Pending]: "warning",
  [BillDisplayStatus.Paid]: "success",
  [BillDisplayStatus.Void]: "error",
  [BillDisplayStatus.Failed]: "error",
  [BillDisplayStatus.Refunded]: "success",
  [BillDisplayStatus.PartiallyRefunded]: "success",
  [BillDisplayStatus.Credit]: "success",
  [BillDisplayStatus.Deferred]: "default",
};

export enum TransactionStatus {
  Pending = "Pending",
  Success = "Success",
  Failed = "Failed",
}

export const transactionStatusLabels: Record<TransactionStatus, string> = {
  [TransactionStatus.Pending]: "Pending Payment",
  [TransactionStatus.Success]: "Payment",
  [TransactionStatus.Failed]: "Failed Payment",
};

export function getTransactionStatusLabel(
  status: TransactionStatus | string,
): string {
  if (isEnumValue(status, TransactionStatus)) {
    return transactionStatusLabels[status as TransactionStatus];
  }
  return status;
}

export enum ShopifySubscriptionStatus {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  DECLINED = "DECLINED",
  EXPIRED = "EXPIRED",
  FROZEN = "FROZEN",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
}

export const shopifySubscriptionStatusLabels: Record<
  ShopifySubscriptionStatus,
  string
> = {
  [ShopifySubscriptionStatus.ACTIVE]: "Active",
  [ShopifySubscriptionStatus.CANCELLED]: "Cancelled",
  [ShopifySubscriptionStatus.DECLINED]: "Declined",
  [ShopifySubscriptionStatus.EXPIRED]: "Expired",
  [ShopifySubscriptionStatus.FROZEN]: "Frozen",
  [ShopifySubscriptionStatus.PENDING]: "Pending",
  [ShopifySubscriptionStatus.ACCEPTED]: "Accepted",
};

export function getShopifySubscriptionStatusLabel(
  status: ShopifySubscriptionStatus | string,
): string {
  if (isEnumValue(status, ShopifySubscriptionStatus)) {
    return shopifySubscriptionStatusLabels[status as ShopifySubscriptionStatus];
  }
  return status;
}

export type ShopifySubscriptionStatusColor =
  | "success"
  | "warning"
  | "error"
  | "gray";

export const shopifySubscriptionStatusColors: Record<
  ShopifySubscriptionStatus,
  ShopifySubscriptionStatusColor
> = {
  [ShopifySubscriptionStatus.ACTIVE]: "success",
  [ShopifySubscriptionStatus.CANCELLED]: "error",
  [ShopifySubscriptionStatus.DECLINED]: "error",
  [ShopifySubscriptionStatus.EXPIRED]: "gray",
  [ShopifySubscriptionStatus.FROZEN]: "warning",
  [ShopifySubscriptionStatus.PENDING]: "warning",
  [ShopifySubscriptionStatus.ACCEPTED]: "success",
};

export function getShopifySubscriptionStatusColor(
  status: ShopifySubscriptionStatus | string,
): ShopifySubscriptionStatusColor {
  if (isEnumValue(status, ShopifySubscriptionStatus)) {
    return shopifySubscriptionStatusColors[status as ShopifySubscriptionStatus];
  }
  return "gray";
}

export const subscriptionProductLabels: Record<SubscriptionProduct, string> = {
  [SubscriptionProduct.MarketingSms]: "Marketing SMS",
  [SubscriptionProduct.MarketingEmail]: "Marketing Email",
  [SubscriptionProduct.OrderTracking]: "Order Tracking",
  [SubscriptionProduct.ReturnAiExchange]: "AI Exchange",
  [SubscriptionProduct.Returns]: "Returns",
  [SubscriptionProduct.CheckoutOptimization]: "Checkout Optimization",
  [SubscriptionProduct.Aeo]: "AEO",
  [SubscriptionProduct.Ims]: "IMS",
  [SubscriptionProduct.ImsForecasting]: "IMS Forecasting",
  [SubscriptionProduct.Recover]: "Recover",
  [SubscriptionProduct.SupportBase]: "Support",
  [SubscriptionProduct.SupportAi]: "Support AI",
  [SubscriptionProduct.SupportSms]: "Support SMS",
  [SubscriptionProduct.SupportVoice]: "Support Voice",
  [SubscriptionProduct.Oms]: "OMS",
  [SubscriptionProduct.Wms]: "WMS",
  [SubscriptionProduct.Disputes]: "Disputes",
  [SubscriptionProduct.Lcg]: "LCG",
  [SubscriptionProduct.Ctx]: "Contextual",
  [SubscriptionProduct.DimensionalPackaging]: "Dimensional Packaging",
  [SubscriptionProduct.Supplies]: "Supplies",
};

export function getSubscriptionProductLabel(
  product: SubscriptionProduct | string,
): string {
  if (isEnumValue(product, SubscriptionProduct)) {
    return subscriptionProductLabels[product as SubscriptionProduct];
  }
  return product;
}

export type SubscriptionProductBadgeColor = ChargeTypeBadgeColor;

export const subscriptionProductColors: Record<
  SubscriptionProduct,
  SubscriptionProductBadgeColor
> = {
  [SubscriptionProduct.MarketingSms]: "pink",
  [SubscriptionProduct.MarketingEmail]: "pink",
  [SubscriptionProduct.OrderTracking]: "orange",
  [SubscriptionProduct.ReturnAiExchange]: "blue",
  [SubscriptionProduct.Returns]: "blue",
  [SubscriptionProduct.CheckoutOptimization]: "orange",
  [SubscriptionProduct.Aeo]: "brand",
  [SubscriptionProduct.Ims]: "indigo",
  [SubscriptionProduct.ImsForecasting]: "indigo",
  [SubscriptionProduct.Recover]: "grayBlue",
  [SubscriptionProduct.SupportBase]: "purple",
  [SubscriptionProduct.SupportAi]: "purple",
  [SubscriptionProduct.SupportSms]: "purple",
  [SubscriptionProduct.SupportVoice]: "purple",
  [SubscriptionProduct.Oms]: "blueLight",
  [SubscriptionProduct.Wms]: "blueLight",
  [SubscriptionProduct.Disputes]: "success",
  [SubscriptionProduct.Lcg]: "blueLight",
  [SubscriptionProduct.Ctx]: "brand",
  [SubscriptionProduct.DimensionalPackaging]: "orange",
  [SubscriptionProduct.Supplies]: "blueLight",
};

export function getSubscriptionProductColor(
  product: SubscriptionProduct | string,
): SubscriptionProductBadgeColor {
  if (isEnumValue(product, SubscriptionProduct)) {
    return subscriptionProductColors[product as SubscriptionProduct];
  }
  return "gray";
}

export enum PaymentProvider {
  Stripe = "Stripe",
  Shopify = "Shopify",
}

export enum PaymentMethodType {
  Card = "Card",
  UsBankAccount = "UsBankAccount",
  ShopifyAppSubscription = "ShopifyAppSubscription",
}

export enum PaymentMethodPriority {
  Primary = "Primary",
  Secondary = "Secondary",
  ShopifySubscription = "ShopifySubscription",
  Deactivated = "Deactivated",
}
